import React from 'react';
import { FiMaximize2, FiSquare, FiCrosshair, FiMapPin, FiTrash2, FiX } from 'react-icons/fi';
import { formatDistance, formatFeet, formatArea } from '../../utils/measurementUtils';
import { formatLatLng, formatStatePlane, formatDMS } from '../../utils/coordinateUtils';

const toolButtonStyle = (isActive) => ({
  width: '44px',
  height: '44px',
  backgroundColor: isActive ? '#1e40af' : '#2563eb',
  color: 'white',
  border: isActive ? '2px solid #93c5fd' : 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
  transition: 'background-color 0.2s',
  minHeight: '44px'
});

const readoutPanelStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
  padding: '12px',
  minWidth: '220px',
  maxWidth: '280px',
  fontSize: '13px',
  marginTop: '8px'
};

const clearButtonStyle = {
  padding: '6px 12px',
  backgroundColor: '#ef4444',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const finishButtonStyle = {
  padding: '8px 16px',
  backgroundColor: '#16a34a',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  width: '100%',
  marginTop: '8px'
};

function MeasurementToolbar({
  activeTool,
  onToolChange,
  measurementData,
  onClear,
  onFinish,
  onOpenGps,
  isMobile
}) {
  const toggleTool = (tool) => {
    if (activeTool === tool) {
      onToolChange(null);
      onClear();
    } else {
      onClear();
      onToolChange(tool);
    }
  };

  const tools = [
    { id: 'distance', icon: FiMaximize2, title: 'Measure Distance' },
    { id: 'area', icon: FiSquare, title: 'Measure Area' },
    { id: 'coordinate', icon: FiCrosshair, title: 'Show Coordinates' },
    { id: 'gps', icon: FiMapPin, title: 'Drop Pin at Coordinates' }
  ];

  return (
    <div style={{
      position: 'absolute',
      left: '15px',
      top: '15px',
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    }}>
      {/* Tool Buttons */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        gap: '6px'
      }}>
        {tools.map(tool => (
          <button
            key={tool.id}
            onClick={() => tool.id === 'gps' ? onOpenGps() : toggleTool(tool.id)}
            style={toolButtonStyle(activeTool === tool.id)}
            title={tool.title}
          >
            <tool.icon size={20} />
          </button>
        ))}
      </div>

      {/* Readout Panel */}
      {activeTool && activeTool !== 'gps' && (
        <div style={readoutPanelStyle}>
          {/* Distance Readout */}
          {activeTool === 'distance' && (
            <div>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#ef4444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Distance</span>
                <button onClick={() => { onClear(); }} style={{ ...clearButtonStyle, padding: '4px 8px' }}>
                  <FiTrash2 size={12} /> Clear
                </button>
              </div>

              {measurementData.distanceSegments.length > 0 ? (
                <>
                  {measurementData.distanceSegments.length > 1 && (
                    <div style={{ maxHeight: '120px', overflowY: 'auto', marginBottom: '8px' }}>
                      {measurementData.distanceSegments.map((seg, idx) => (
                        <div key={idx} style={{ padding: '2px 0', color: '#666', fontSize: '12px' }}>
                          Seg {idx + 1}: {formatDistance(seg)}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', fontWeight: '600', fontSize: '15px' }}>
                    Total: {formatDistance(measurementData.distanceTotal)}
                  </div>
                </>
              ) : (
                <div style={{ color: '#999', fontSize: '12px' }}>
                  Click on the map to start measuring.
                  {!isMobile && ' Double-click to finish.'}
                </div>
              )}

              {isMobile && measurementData.distancePoints.length >= 2 && (
                <button onClick={onFinish} style={finishButtonStyle}>
                  Finish Measurement
                </button>
              )}
            </div>
          )}

          {/* Area Readout */}
          {activeTool === 'area' && (
            <div>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#8b5cf6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Area</span>
                <button onClick={() => { onClear(); }} style={{ ...clearButtonStyle, padding: '4px 8px', backgroundColor: '#8b5cf6' }}>
                  <FiTrash2 size={12} /> Clear
                </button>
              </div>

              {measurementData.areaValue > 0 ? (
                <div>
                  {(() => {
                    const { sqFt, acres } = formatArea(measurementData.areaValue);
                    return (
                      <>
                        <div style={{ fontSize: '15px', fontWeight: '600' }}>{sqFt} sq ft</div>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#8b5cf6' }}>{acres} acres</div>
                        {measurementData.areaPerimeter > 0 && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
                            Perimeter: {formatFeet(measurementData.areaPerimeter)}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ color: '#999', fontSize: '12px' }}>
                  Click to place vertices (min 3).
                  {!isMobile && ' Double-click to close polygon.'}
                </div>
              )}

              {isMobile && measurementData.areaPoints.length >= 3 && measurementData.areaValue === 0 && (
                <button onClick={onFinish} style={finishButtonStyle}>
                  Close Polygon
                </button>
              )}
            </div>
          )}

          {/* Coordinate Display */}
          {activeTool === 'coordinate' && (
            <div>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#0ea5e9' }}>
                Coordinates
              </div>

              {measurementData.cursorCoords ? (
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Lat / Lng</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {formatLatLng(measurementData.cursorCoords.lat)} N
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {formatLatLng(measurementData.cursorCoords.lng)} W
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#888', marginTop: '2px' }}>
                      {formatDMS(measurementData.cursorCoords.lat, true)}, {formatDMS(measurementData.cursorCoords.lng, false)}
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                      GA State Plane ({measurementData.cursorCoords.zone === 'west' ? 'West' : 'East'})
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      N: {formatStatePlane(measurementData.cursorCoords.northing)} ft
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      E: {formatStatePlane(measurementData.cursorCoords.easting)} ft
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#999', fontSize: '12px' }}>
                  {isMobile ? 'Tap the map to see coordinates.' : 'Move cursor over the map.'}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MeasurementToolbar;
